import { TAG_TYPES } from './rtk/constants';
import { postLineItemsByLineItemIdSetordinalEnhancer } from './optimistic-updates/endpoints';
import type { components } from './fetch/fetch.generated';
import type * as Types from './rtk/rtk.generated';
import { surveyApi as _surveyApi } from './rtk/rtk.generated';

export { QUESTION_RESULT_TYPE } from './constants';

export type { Types as SurveyApi };

type Schemas = components['schemas'];

// Schema types for endpoints excluded from RTK codegen (SurveyApi namespace no longer includes these)
export type AlienationAnalysisResponse = Schemas['AlienationAnalysisResponse'];
export type AlienationGroup = Schemas['AlienationGroup'];
export type PenaltyAnalysisDto = Schemas['PenaltyAnalysisDto'];
export type PenaltyAnalysis = Schemas['PenaltyAnalysis'];
export type PenaltyStatus = Schemas['PenaltyStatus'];
export type Group = Schemas['Group'];
export type AttributeResult = Schemas['AttributeResult'];

// Alias named type import — schema types kept on fetch client when RTK endpoints removed
export type {
  Block,
  BlockBehavior,
  BlockType,
  ConfigureSurveyRequest,
  GroupType,
  LineItem,
  LineItemRead,
  LineItemScope,
  MaterializedSurveyDto,
  MaterializedSurveyGroupData,
  Question,
  QuestionDesign,
  QuestionDesignChoice,
  ScoreCardEntry,
  Slot,
  SurveyConfig,
  SurveyConfigBlock,
  QuestionLabel,
  QuestionChoice,
  ResultContextDto,
  // VariableLogics
  VariableLogic,
  VariableLogicValidationFailure,
  VariableLogicValidationResults,
  // Photo QC Review
  PhotoQcReviewDto,
  PhotoQcReviewDtoPagedResult,
} from './rtk/rtk.generated';

export type BannerPoint = Schemas['BannerPoint'];
export type BlockTypeOption = Schemas['BlockTypeOption'];
export type CrossTab = Schemas['CrossTab'];
export type CrossTabsDto = Schemas['CrossTabsDto'];
export type IssueSeverity = Schemas['IssueSeverity'];
export type ReviewIssue = Schemas['ReviewIssue'];
export type SurveyReviewAllRequest = Schemas['SurveyReviewAllRequest'];
export type SurveyReviewResult = Schemas['SurveyReviewResult'];
export type SurveyReviewAllResult = Schemas['SurveyReviewAllResult'];

export * from './errors';

export const surveyApi = _surveyApi.enhanceEndpoints({
  endpoints: {
    // Blocks enhancer (inlined)
    postBlueprintsBulkCreate: {
      invalidatesTags: [TAG_TYPES.BLOCK],
    },
    getBlocksByBlockIdSlots: {
      providesTags: (_result, _error, arg) => [{ type: TAG_TYPES.BLOCK, id: arg.blockId }],
    },
    postBlocksByBlockIdSlotsAssign: {
      invalidatesTags: (_result, _error, arg) => [{ type: TAG_TYPES.BLOCK, id: arg.blockId }],
    },

    // Line Items enhancer (inlined)
    postLineItemsByLineItemIdSetordinal: postLineItemsByLineItemIdSetordinalEnhancer(_surveyApi),

    // Questions enhancer (inlined)
    getSurveyConfigsBySurveyConfigIdQuestions: {
      providesTags: (_result, _error, arg) => [
        { type: TAG_TYPES.SURVEY_QUESTIONS, id: arg.surveyConfigId },
      ],
    },

    // Survey Configs enhancer (inlined)
    postSurveyConfigsBySurveyConfigIdStartreporting: {
      invalidatesTags: [],
    },
    postSurveyConfigsBySurveyConfigIdGenerateTestResponses: {
      invalidatesTags: ['QualityControl'], // Refresh QC data after generation
    },

    // Download endpoints return a file Blob; the generated definitions default to JSON
    // parsing, so override the response handling here (matching the project-api convention).
    // Generating/downloading a file does not mutate survey state, so keep them out of the
    // cache-tag system to avoid spurious refetches.
    postSurveyConfigsBySurveyConfigIdExportDocx: {
      query: (queryArg) => ({
        url: `/SurveyConfigs/${queryArg.surveyConfigId}/$exportDocx`,
        method: 'POST',
        params: { projectId: queryArg.projectId },
        cache: 'no-cache',
        responseHandler: async (response) => response.blob(),
      }),
      transformResponse: (response: Blob) => response,
      invalidatesTags: [],
    },
    getSurveyConfigsBySurveyConfigIdDefinition: {
      query: (queryArg) => ({
        url: `/SurveyConfigs/${queryArg.surveyConfigId}/$definition`,
        cache: 'no-cache',
        responseHandler: async (response) => response.blob(),
      }),
      transformResponse: (response: Blob) => response,
      providesTags: [],
    },
  },
});

/**
 *  @deprecated use exports from {@link surveyApi} directly instead
 */
export const {
  usePutBlocksByBlockIdMutation: useUpdateBlockByBlockIdMutation,
  useGetBlocksByBlockIdSlotsQuery: useGetSlotsByBlockIdQuery,
  usePostBlocksByBlockIdSlotsAssignMutation: useAssignSlotsByBlockIdMutation,
  usePostBlocksBulkGetMutation: useGetBlocksBulkMutation,
  usePostBlueprintsBulkCreateMutation: useCreateBlocksViaCsvMutation,
  usePostHighlighterProfileSurveysMutation: useCreateHighlighterProfileSurveys,
  usePostHighlighterProfileSurveysFinalizeQuestionsMutation:
    useProfileSurveysFinalizeQuestionsMutation,
  useGetHighlighterProfileSurveysQuery: useProfileSurveyQuery,
  usePatchHighlighterProfileSurveysMutation: useUpdateProfileSurvey,
  useDeleteLineItemsByLineItemIdMutation: useDeleteLineItemMutation,
  useGetQuestionDesignsByQuestionDesignIdChoicesQuery: useGetQuestionDesignChoicesQuery,
  usePostQuestionDesignsByQuestionDesignIdChoicesAssignMutation:
    useAssignQuestionDesignChoicesMutation,
  usePutQuestionDesignsByQuestionDesignIdMutation: useUpdateQuestionDesignMutation,
  usePostQuestionDesignsMutation: useCreateQuestionDesignMutation,
  useGetSurveyConfigsBySurveyConfigIdQuestionsQuery: useGetSurveyConfigQuestionsQuery,
  useGetSurveyConfigsBySurveyConfigIdResponseTagCountsQuery:
    useGetSurveyConfigResponseTagCountsQuery,
  useLazyGetSurveyConfigsBySurveyConfigIdQuestionsQuery: useLazyGetSurveyConfigQuestionsQuery,
  usePostQuestionsByQuestionIdConvertMutation: useConvertSurveyQuestionMutation,
  usePostSurveyConfigsConfigureMutation: useConfigureSurveyConfigMutation,
  usePostSurveyConfigsBySurveyConfigIdStartreportingMutation: useImportSurveyConfigMutation,
  usePostSurveyConfigsBySurveyConfigIdResetresultschemaMutation: useResetResultSchemaMutation,
  usePostSurveyConfigsBySurveyConfigIdGenerateTestResponsesMutation:
    useGenerateTestResponsesMutation,
  useGetAiGenerationJobByJobIdStatusQuery: useGetAIGenerationJobStatusQuery,
  usePutLineItemsMutation,
  usePostLineItemsByLineItemIdSetordinalMutation: setLineItemOrdinal,
  useGetResultContextsByResultContextIdLineitemsQuery,
  usePostBlocksMutation,
  useDeleteBlocksByBlockIdMutation,
  useGetQuestionDesignsQuery,
  useGetBlocksQuery,
  useDeleteScoreCardEntriesByScorecardEntryIdMutation: useDeleteScorecardEntriesMutation,
  usePutScoreCardEntriesMutation,
  useGetResultContextsByResultContextIdScorecardentriesQuery: useGetScorecardEntriesQuery,
  useLazyGetResultContextsByResultContextIdScorecardentriesQuery: useLazyGetScorecardEntriesQuery,
  usePostScoreCardEntriesMutation: useCreateScorecardEntryMutation,
  useGetBlocksQuestionDesignByQuestionDesignIdQuery,
} = surveyApi;
